import React, { useState, useRef, useEffect } from 'react';
import { 
  ArrowLeft, Edit2, Edit3, Trash2, Copy, FileText, Download, Share2, 
  MoreVertical, Plus, Wand2, Upload, GripVertical, CheckCircle2, 
  ChevronDown, Check, FileDown, BookOpen, Globe, Lock, Loader2, 
  AlertCircle, Crown, FileSpreadsheet, Sparkles, Layers 
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '../ui/Button';
import { useAuth } from '../../AuthProvider';
import { useAiHeavyJob } from '../../context/AiHeavyJobContext';
import { ManualQuestionModal } from './ManualQuestionModal';
import { CreateQuestionSetModal } from './CreateQuestionSetModal';
import { ConfirmDeleteModal } from './ConfirmDeleteModal';
import { QuotaExceededModal } from './QuotaExceededModal';
import { ExcelImportModal } from './ExcelImportModal';

import { 
  useQuestionSetDetail, 
  useCloneQuestionSet, 
  useDeleteQuestionSet, 
  useDeleteQuestion 
} from './hooks';

// Type declarations...
interface QuestionItem {
  id: string;
  _id?: string;
  questionText: string;
  options: any[];
  correctAnswer: string;
  explanation?: string;
  difficulty?: 'easy' | 'medium' | 'hard';
  setId?: string;
}

interface QuestionSet {
  id: string;
  _id?: string;
  name?: string;
  title?: string;
  description?: string;
  subject?: string;
  grade?: string;
  isPublic: boolean;
  userId?: string;
  userEmail?: string;
  questionCount?: number;
}

interface QuestionSetDetailProps {
  setId: string;
  onBack: () => void;
  onOpenTopUp?: () => void;
}

export const QuestionSetDetail: React.FC<QuestionSetDetailProps> = ({ setId, onBack, onOpenTopUp }) => {
  const { user: authUser } = useAuth();
  
  // Custom Hooks
  const { data: detailData, isLoading: loading, error: queryError, refetch: fetchSetDetails } = useQuestionSetDetail(setId);
  const cloneMutation = useCloneQuestionSet();
  const deleteSetMutation = useDeleteQuestionSet();
  const deleteQuestionMutation = useDeleteQuestion();

  const isCloning = cloneMutation.isPending;

  const currentSet = detailData?.setInfo as QuestionSet | undefined;
  const questions = (detailData?.questions as QuestionItem[]) || [];
  const error = queryError?.message || null;
  const quota = detailData?.quota;

  const { openModal: openAiModal } = useAiHeavyJob();

  // States for Modals
  const [isManualModalOpen, setIsManualModalOpen] = useState(false);
  const [isExcelImportModalOpen, setIsExcelImportModalOpen] = useState(false);
  
  const [editingQuestion, setEditingQuestion] = useState<QuestionItem | null>(null);
  const [deletingQuestion, setDeletingQuestion] = useState<QuestionItem | null>(null);
  
  const [isEditSetModalOpen, setIsEditSetModalOpen] = useState(false);
  const [isConfirmDeleteSetOpen, setIsConfirmDeleteSetOpen] = useState(false);
  
  const [notification, setNotification] = useState<string | null>(null);
  const successMessage = notification;

  const handleAddQuestionClick = () => {
    setEditingQuestion(null);
    setIsManualModalOpen(true);
  };

  const handleEditQuestion = (q: QuestionItem) => {
    setEditingQuestion(q);
    setIsManualModalOpen(true);
  };

  const setQuestionToDelete = (q: QuestionItem | null) => {
    setDeletingQuestion(q);
  };

  // Export Menu State
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  // Quota Modals
  const [quotaExceededModal, setQuotaExceededModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'set' | 'question' | 'general';
  }>({
    isOpen: false,
    title: 'Đã đạt giới hạn số lượng câu hỏi',
    message: '',
    type: 'question'
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target as Node)) {
        setShowExportMenu(false);
      }
    };
    if (showExportMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showExportMenu]);
  const handleCloneCurrentSet = async () => {
    if (!currentSet) return;
    try {
      await cloneMutation.mutateAsync(currentSet.id);
      setNotification(`Nhân bản thành công bộ câu hỏi "${currentSet.name || currentSet.title}" về kho của bạn!`);
      setTimeout(() => setNotification(null), 4000);
    } catch (err: any) {
      if (err.message.includes('giới hạn')) {
        setQuotaExceededModal({
          isOpen: true,
          title: 'Đã đạt giới hạn số lượng bộ câu hỏi',
          message: err.message || 'Tài khoản của Thầy/Cô đã đạt giới hạn tối đa số lượng bộ câu hỏi. Vui lòng nâng cấp VIP/SVIP để nhân bản thêm!',
          type: 'set'
        });
      }
    }
  };

  const confirmDeleteQuestion = async () => {
    if (!deletingQuestion) return;
    const questionId = deletingQuestion._id || deletingQuestion.id;
    if (!questionId) {
      toast.error("Không tìm thấy ID câu hỏi");
      return;
    }
    try {
      await deleteQuestionMutation.mutateAsync(questionId);
      setNotification('Đã xóa câu hỏi thành công.');
      setTimeout(() => setNotification(null), 4000);
      setDeletingQuestion(null);
    } catch (err: any) {
      console.error(err);
    }
  };

  const confirmDeleteSet = async () => {
    if (!currentSet) return;
    try {
      await deleteSetMutation.mutateAsync(currentSet.id);
      setIsConfirmDeleteSetOpen(false);
      onBack();
    } catch (err: any) {
      console.error(err);
    }
  };

  const handleExportDocx = () => {
    // We can assume logic is same.
    setShowExportMenu(false);
    
    // Create docx content...
    let content = `BỘ CÂU HỎI: ${currentSet?.name || currentSet?.title}\n`;
    content += `Môn: ${currentSet?.subject || 'Khác'} | Lớp: ${currentSet?.grade || 'Khác'}\n`;
    content += `\n`;
    
    questions.forEach((q, i) => {
      content += `Câu ${i + 1}: ${q.questionText}\n`;
      q.options.forEach((opt, j) => {
        const letter = String.fromCharCode(65 + j);
        content += `${letter}. ${opt}\n`;
      });
      content += `\nĐáp án: ${q.correctAnswer}\n`;
      if (q.explanation) {
        content += `Giải thích: ${q.explanation}\n`;
      }
      content += `\n-------------------------\n\n`;
    });
    
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Bo_cau_hoi_${currentSet?.name || 'export'}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    setNotification("Đã xuất file thành công (Định dạng .txt tạm thời).");
    setTimeout(() => setNotification(null), 4000);
  };
  const currentUserId = authUser?.id || authUser?._id || authUser?.uid || '';
  const currentUserEmail = authUser?.email || '';

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <Loader2 className="w-10 h-10 text-[#236539] animate-spin" />
        <p className="text-sm font-medium text-slate-500">Đang tải chi tiết bộ câu hỏi...</p>
      </div>
    );
  }

  if (error || !currentSet) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center max-w-md mx-auto my-12 space-y-4 shadow-xs">
        <div className="w-12 h-12 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center mx-auto">
          <AlertCircle className="w-6 h-6" />
        </div>
        <h3 className="text-lg font-bold text-slate-800">Không tìm thấy bộ câu hỏi</h3>
        <p className="text-sm text-slate-500">{error || 'Bộ câu hỏi này không tồn tại hoặc bạn không có quyền truy cập.'}</p>
        <Button onClick={onBack} variant="secondary" className="cursor-pointer">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Quay lại danh sách
        </Button>
      </div>
    );
  }

  const isOwner = Boolean(
    currentUserId && currentSet?.userId && (
      String(currentSet.userId) === String(currentUserId) ||
      (currentUserEmail && String(currentSet.userId).toLowerCase() === currentUserEmail.toLowerCase())
    )
  );

  const isAdmin = authUser?.role === 'admin' || (Array.isArray(authUser?.roles) && authUser.roles.includes('admin'));
  const canEdit = isOwner || isAdmin;

  // Quota & Tier calculation
  const userVipTierNum = typeof authUser?.vipTier === 'number' 
    ? authUser.vipTier 
    : (authUser?.vipTier === 'SVIP' ? 2 : authUser?.vipTier === 'VIP' ? 1 : Number(authUser?.vipTier) || 0);

  const effectiveTier: 'USER' | 'VIP' | 'SVIP' = 
    (isAdmin || userVipTierNum === 2 || authUser?.vipTier === 'SVIP' || quota?.userTier === 'SVIP') 
      ? 'SVIP' 
      : (userVipTierNum === 1 || authUser?.vipTier === 'VIP' || quota?.userTier === 'VIP') 
        ? 'VIP' 
        : (quota?.userTier || 'USER');

  const defaultMaxQuestions = effectiveTier === 'SVIP' ? 300 : effectiveTier === 'VIP' ? 200 : 100;
  const maxQ = (quota?.maxQuestions && quota.userTier === effectiveTier) ? quota.maxQuestions : defaultMaxQuestions;
  const currQ = questions.length;
  const quotaPercent = Math.min(100, Math.round((currQ / maxQ) * 100));
  const isQuotaFull = currQ >= maxQ;

  const authorName = (typeof currentSet?.userId === 'object' && currentSet?.userId?.displayName)
    ? currentSet.userId.displayName
    : (currentSet?.userName || currentSet?.user?.displayName || 'Thầy/Cô');

  const authorAvatar = (typeof currentSet?.userId === 'object' && (currentSet?.userId?.avatar || currentSet?.userId?.photoURL))
    ? (currentSet.userId.avatar || currentSet.userId.photoURL)
    : (currentSet?.userAvatar || currentSet?.user?.avatar || currentSet?.user?.photoURL);

  return (
    <div className="space-y-6">
      {/* Top Header & Breadcrumb */}
      {!canEdit ? (
        /* Unified Header for Community View */
        <div className="bg-gradient-to-r from-amber-50/50 via-emerald-50/30 to-teal-50/50 rounded-2xl border border-amber-200/80 p-5 shadow-xs flex flex-col md:flex-row items-start justify-between gap-4 relative overflow-hidden">
          <div className="flex items-start space-x-4 z-10 w-full">
            <button
              type="button"
              onClick={onBack}
              className="p-2.5 bg-white hover:bg-slate-50 border border-slate-200 rounded-xl text-slate-600 hover:text-slate-900 transition-colors shadow-2xs cursor-pointer shrink-0 mt-1"
              title="Quay lại danh sách"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="space-y-3.5 flex-1">
              <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                <span className="px-2 py-0.5 bg-amber-100 text-amber-900 text-xs font-bold rounded-lg border border-amber-300">
                  Chế độ chỉ xem
                </span>
                <span className="px-2 py-0.5 bg-white text-slate-700 text-xs font-bold rounded-lg border border-slate-200">
                  {currentSet.subject}
                </span>
                <span className="px-2 py-0.5 bg-white text-slate-700 text-xs font-semibold rounded-lg border border-slate-200">
                  {isNaN(Number(currentSet.grade)) ? currentSet.grade : `Lớp ${currentSet.grade}`}
                </span>
                {currentSet.isPublic && (
                  <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[11px] font-bold rounded-full border border-emerald-200 flex items-center space-x-1">
                    <Globe className="w-3 h-3" />
                    <span>Công khai</span>
                  </span>
                )}
              </div>
              
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 leading-snug">
                {currentSet.title || currentSet.name}
              </h2>

              <div className="flex flex-wrap items-center gap-3 pt-1">
                <div className="flex items-center space-x-1.5 text-xs sm:text-sm text-slate-700 bg-white/80 px-2.5 py-1 rounded-full border border-slate-200 shadow-2xs">
                  {authorAvatar ? (
                    <img 
                      src={authorAvatar} 
                      alt="Author" 
                      className="w-5 h-5 rounded-full object-cover ring-1 ring-slate-200"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <div className="w-5 h-5 rounded-full bg-gradient-to-br from-[#236539] to-emerald-700 text-white text-[10px] font-bold flex items-center justify-center">
                      {authorName.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <span>
                    Tác giả: <strong className="text-slate-900">{authorName}</strong>
                  </span>
                </div>

                {typeof currentSet.cloneCount === 'number' && (
                  <div className="flex items-center space-x-1 text-xs sm:text-sm font-medium text-slate-600 bg-white/80 px-2.5 py-1 rounded-full border border-slate-200 shadow-2xs">
                    <Copy className="w-3.5 h-3.5 text-[#236539]" />
                    <span><strong className="text-slate-900">{currentSet.cloneCount}</strong> lượt nhân bản</span>
                  </div>
                )}
              </div>

              {currentSet.description && (
                <div className="p-3 bg-white/60 border border-slate-200/60 rounded-xl max-w-3xl">
                  <p className="text-sm text-slate-700 italic leading-relaxed">
                    {currentSet.description}
                  </p>
                </div>
              )}

              <p className="text-sm text-slate-600 leading-relaxed max-w-3xl pt-1">
                Thầy/Cô đang xem bộ câu hỏi cộng đồng. Để toàn quyền <strong>chỉnh sửa câu hỏi, tạo câu hỏi mới bằng AI hoặc import từ Excel</strong>, vui lòng bấm nút Sao chép để lưu một bản sao vào thư viện cá nhân.
              </p>
            </div>
          </div>

          <div className="w-full md:w-auto shrink-0 z-10 flex justify-end md:mt-2">
            <button
              type="button"
              disabled={isCloning}
              onClick={handleCloneCurrentSet}
              className="w-full md:w-auto px-5 py-3 bg-[#236539] hover:bg-[#1a4e2b] text-white font-bold text-sm rounded-xl shadow-md hover:shadow-lg transition-all flex items-center justify-center space-x-2 cursor-pointer active:scale-95 ring-2 ring-[#236539]/20"
            >
              {isCloning ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Copy className="w-5 h-5 text-emerald-200" />
              )}
              <span>{isCloning ? 'Đang sao chép...' : 'Sao chép bộ câu hỏi'}</span>
            </button>
          </div>
        </div>
      ) : (
        /* Top Header & Breadcrumb for My Library */
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={onBack}
              className="p-2.5 bg-white hover:bg-slate-100 border border-slate-200 rounded-xl text-slate-600 hover:text-slate-900 transition-colors shadow-2xs cursor-pointer"
              title="Quay lại danh sách"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <div className="flex items-center space-x-2">
                <span className="px-2 py-0.5 bg-slate-100 text-slate-700 text-xs font-bold rounded-lg border border-slate-200">
                  {currentSet.subject}
                </span>
                <span className="px-2 py-0.5 bg-slate-100 text-slate-700 text-xs font-semibold rounded-lg border border-slate-200">
                  {isNaN(Number(currentSet.grade)) ? currentSet.grade : `Lớp ${currentSet.grade}`}
                </span>
                {currentSet.isPublic ? (
                  <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[11px] font-bold rounded-full border border-emerald-200 flex items-center space-x-1">
                    <Globe className="w-3 h-3" />
                    <span>Công khai</span>
                  </span>
                ) : (
                  <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-[11px] font-bold rounded-full border border-slate-200 flex items-center space-x-1">
                    <Lock className="w-3 h-3" />
                    <span>Riêng tư</span>
                  </span>
                )}
              </div>
              <h2 className="text-xl font-bold text-slate-900 mt-1">
                {currentSet.title || currentSet.name}
              </h2>
              {currentSet.description && (
                <div className="mt-2 p-2.5 bg-slate-50 border border-slate-200 rounded-lg max-w-2xl">
                  <p className="text-xs sm:text-sm text-slate-600 italic leading-relaxed">
                    {currentSet.description}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Button
              variant="secondary"
              onClick={() => setIsEditSetModalOpen(true)}
              className="text-xs flex items-center space-x-1.5 shadow-2xs cursor-pointer"
            >
              <Edit3 className="w-3.5 h-3.5" />
              <span>Sửa thông tin bộ</span>
            </Button>

            <Button
              variant="ghost"
              onClick={() => setIsConfirmDeleteSetOpen(true)}
              className="text-xs text-rose-600 hover:text-rose-700 hover:bg-rose-50 flex items-center space-x-1.5 border border-rose-200/80 cursor-pointer"
            >
              <Trash2 className="w-3.5 h-3.5" />
              <span>Xóa bộ này</span>
            </Button>
          </div>
        </div>
      )}

      {/* Success Notification */}
      {successMessage && (
        <div className="p-3.5 bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold rounded-xl flex items-center space-x-2 animate-in fade-in">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          <span>{successMessage}</span>
        </div>
      )}

      {/* Error Notification */}
      {error && (
        <div className="p-3.5 bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold rounded-xl flex items-center space-x-2 animate-in fade-in">
          <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Quota & Main Actions Banner */}
      {canEdit && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-xs space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
            {/* Left: Quota Progress */}
            <div className="md:col-span-5 space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="font-bold text-slate-700">
                  Dung lượng bộ câu hỏi: <strong className="text-[#236539]">{currQ}</strong> / {maxQ} câu
                </span>
                <span className="font-mono font-bold text-slate-500">
                  {effectiveTier}
                </span>
              </div>

              {/* Progress bar */}
              <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${
                    quotaPercent >= 90
                      ? 'bg-rose-500'
                      : quotaPercent >= 70
                      ? 'bg-amber-500'
                      : 'bg-[#236539]'
                  }`}
                  style={{ width: `${quotaPercent}%` }}
                />
              </div>

              <p className="text-[11px] text-slate-400">
                Hạn mức tối đa: USER (100 câu), VIP (200 câu), SVIP (300 câu).
                {effectiveTier !== 'SVIP' && onOpenTopUp && (
                  <button
                    type="button"
                    onClick={onOpenTopUp}
                    className="text-amber-700 hover:text-amber-800 font-bold ml-1 inline-flex items-center cursor-pointer underline"
                  >
                    <Crown className="w-3 h-3 mr-0.5 text-amber-600" />
                    {effectiveTier === 'VIP' ? 'Nâng cấp SVIP' : 'Nâng cấp VIP'}
                  </button>
                )}
              </p>
            </div>

            {/* Right: 3 Main Action Buttons */}
            <div className="md:col-span-7 flex flex-wrap items-center justify-start md:justify-end gap-2.5">
              {/* 1. Thêm thủ công */}
              <button
                type="button"
                onClick={handleAddQuestionClick}
                disabled={isQuotaFull}
                className={`px-3.5 py-2.5 rounded-xl border transition-all flex items-center space-x-1.5 ${
                  isQuotaFull 
                    ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed opacity-70' 
                    : 'bg-white hover:bg-slate-50 text-slate-800 border-slate-300 shadow-2xs hover:shadow-xs cursor-pointer font-bold'
                } text-xs`}
              >
                <Plus className={`w-4 h-4 ${isQuotaFull ? 'text-slate-400' : 'text-[#236539]'}`} />
                <span>Thêm câu hỏi thủ công</span>
              </button>

              {/* 2. Import từ Excel */}
              <button
                type="button"
                onClick={() => setIsExcelImportModalOpen(true)}
                disabled={isQuotaFull}
                className={`px-3.5 py-2.5 rounded-xl border transition-all flex items-center space-x-1.5 ${
                  isQuotaFull
                    ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed opacity-70'
                    : 'bg-white hover:bg-emerald-50 text-emerald-800 border-emerald-300 shadow-2xs hover:shadow-xs cursor-pointer font-bold'
                } text-xs`}
              >
                <FileSpreadsheet className={`w-4 h-4 ${isQuotaFull ? 'text-slate-400' : 'text-emerald-700'}`} />
                <span>Import từ Excel</span>
              </button>

              {/* 3. Tạo nhanh bằng AI */}
              <button
                type="button"
                onClick={() => openAiModal(currentSet)}
                disabled={isQuotaFull}
                className={`px-4 py-2.5 rounded-xl transition-all flex items-center space-x-1.5 ${
                  isQuotaFull
                    ? 'bg-slate-300 text-slate-500 cursor-not-allowed opacity-70 font-bold'
                    : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white shadow-md hover:shadow-lg cursor-pointer active:scale-95 font-black'
                } text-xs`}
              >
                <Sparkles className={`w-4 h-4 ${isQuotaFull ? 'text-slate-400' : 'text-yellow-300'}`} />
                <span>Tạo nhanh bằng AI</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Questions List */}
      <div className="space-y-3.5">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-slate-900 flex items-center space-x-2">
            <span>Danh sách câu hỏi</span>
            <span className="px-2 py-0.5 bg-slate-100 text-slate-700 text-xs font-bold rounded-full">
              {questions.length}
            </span>
          </h3>
        </div>

        {questions.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-12 text-center space-y-4">
            <div className="w-14 h-14 rounded-2xl bg-emerald-50 text-[#236539] flex items-center justify-center mx-auto">
              <Layers className="w-7 h-7" />
            </div>
            <div>
              <h4 className="text-base font-bold text-slate-800">
                {canEdit ? 'Bộ câu hỏi hiện chưa có câu nào' : 'Bộ câu hỏi này hiện chưa có câu hỏi nào từ tác giả'}
              </h4>
              <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
                {canEdit
                  ? 'Thầy/Cô có thể tạo nhanh bằng trí tuệ nhân tạo (AI), nhập hàng loạt từ bảng tính Excel, hoặc thêm từng câu thủ công.'
                  : 'Hãy quay lại sau hoặc chọn bộ câu hỏi khác trong thư viện cộng đồng.'}
              </p>
            </div>
            {canEdit && (
              <div className="flex items-center justify-center space-x-3 pt-2">
                <Button
                  onClick={() => openAiModal(currentSet)}
                  disabled={isQuotaFull}
                  className={`text-xs font-bold ${
                    isQuotaFull
                      ? 'bg-slate-300 text-slate-500 cursor-not-allowed opacity-70'
                      : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 text-white shadow-md cursor-pointer'
                  }`}
                >
                  <Sparkles className={`w-3.5 h-3.5 mr-1 ${isQuotaFull ? 'text-slate-400' : 'text-yellow-300'}`} />
                  Tạo nhanh bằng AI
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleAddQuestionClick}
                  disabled={isQuotaFull}
                  className={`text-xs ${isQuotaFull ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
                >
                  Thêm thủ công
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {questions.map((q, idx) => (
              <div
                key={q.id || q._id || idx}
                className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-5 shadow-2xs hover:shadow-xs transition-all space-y-3"
              >
                {/* Header row */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start space-x-3">
                    <span className="w-7 h-7 rounded-xl bg-[#236539] text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                      {idx + 1}
                    </span>
                    <div>
                      <h4 className="text-sm sm:text-base font-bold text-slate-900 leading-snug">
                        {q.questionText}
                      </h4>
                      {q.questionMedia && (
                        <div className="mt-3 relative inline-block rounded-xl overflow-hidden border border-slate-200 shadow-sm max-w-full">
                          <img 
                            src={q.questionMedia} 
                            alt="Ảnh đính kèm câu hỏi" 
                            className="max-h-[250px] object-contain bg-slate-50"
                            loading="lazy"
                          />
                        </div>
                      )}
                      {q.difficulty && (
                        <div className="mt-2">
                          <span className="inline-block text-[10px] font-bold px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 border border-slate-200">
                            Mức độ: {q.difficulty}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions: Edit & Delete Question ONLY IF CAN EDIT */}
                  {canEdit && (
                    <div className="flex items-center space-x-1 shrink-0">
                      <button
                        type="button"
                        onClick={() => handleEditQuestion(q)}
                        title="Chỉnh sửa câu hỏi"
                        className="p-1.5 text-slate-400 hover:text-[#236539] hover:bg-emerald-50 rounded-lg transition-colors cursor-pointer"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setQuestionToDelete(q)}
                        title="Xóa câu hỏi này"
                        className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>

                {/* 4 Options Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                  {Array.isArray(q.options) && q.options.map((opt: any, optIdx: number) => {
                    const isObj = typeof opt === 'object' && opt !== null;
                    const optKey = isObj ? (opt.key || String.fromCharCode(65 + optIdx)) : String.fromCharCode(65 + optIdx);
                    const optText = isObj ? (opt.text ?? '') : String(opt ?? '');
                    const isCorrect = isObj ? Boolean(opt.isCorrect) : (q.correctAnswer === optText || q.correctAnswer === optKey || (isObj && q.correctAnswer === opt.id));

                    return (
                      <div
                        key={isObj ? (opt.id || optIdx) : optIdx}
                        className={`p-3 rounded-xl border text-xs sm:text-sm flex items-start space-x-2.5 transition-colors ${
                          isCorrect
                            ? 'bg-emerald-50/90 border-emerald-300 font-bold text-emerald-950 ring-2 ring-emerald-500/10'
                            : 'bg-slate-50/80 border-slate-200 text-slate-700'
                        }`}
                      >
                        <span
                          className={`w-6 h-6 rounded-lg text-xs font-bold flex items-center justify-center shrink-0 ${
                            isCorrect
                              ? 'bg-[#236539] text-white shadow-2xs'
                              : 'bg-white text-slate-700 border border-slate-200'
                          }`}
                        >
                          {optKey}
                        </span>
                        <span className="flex-1 mt-0.5">{optText}</span>
                        {isCorrect && (
                          <CheckCircle2 className="w-4 h-4 text-emerald-700 shrink-0 mt-0.5" />
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Explanation */}
                {q.explanation && (
                  <div className="p-3 bg-amber-50/60 border border-amber-200/70 rounded-xl text-xs text-amber-900">
                    <span className="font-bold">Giải thích:</span> {q.explanation}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      <ManualQuestionModal
        isOpen={isManualModalOpen}
        onClose={() => {
          setIsManualModalOpen(false);
          setEditingQuestion(null);
        }}
        targetSet={currentSet}
        editingQuestion={editingQuestion}
        onQuotaExceeded={(msg) => {
          setQuotaExceededModal({
            isOpen: true,
            title: 'Đã đạt giới hạn số lượng câu hỏi trong bộ',
            message: msg || 'Bộ câu hỏi đã đạt giới hạn số lượng câu hỏi tối đa cho phép của gói hiện tại.',
            type: 'question'
          });
        }}
        onSuccess={(savedQ) => {
          setNotification(editingQuestion ? 'Đã cập nhật câu hỏi thành công.' : 'Đã thêm câu hỏi mới vào bộ thành công.');
          fetchSetDetails();
        }}
      />

      <ExcelImportModal
        isOpen={isExcelImportModalOpen}
        onClose={() => setIsExcelImportModalOpen(false)}
        targetSet={currentSet}
        onQuotaExceeded={(msg) => {
          setQuotaExceededModal({
            isOpen: true,
            title: 'Đã đạt giới hạn số lượng câu hỏi trong bộ',
            message: msg || 'Số lượng câu hỏi từ file Excel vượt quá hạn mức tối đa cho phép của gói hiện tại.',
            type: 'question'
          });
        }}
        onSuccess={(count) => {
          setNotification(`Đã nhập thành công ${count} câu hỏi từ file Excel!`);
          fetchSetDetails();
        }}
      />

      <CreateQuestionSetModal
        isOpen={isEditSetModalOpen}
        onClose={() => setIsEditSetModalOpen(false)}
        editingSet={currentSet}
        onSuccess={(updated) => {
          fetchSetDetails();
          setNotification('Đã cập nhật thông tin bộ câu hỏi thành công.');
        }}
      />

      {/* Confirm Delete Single Question Modal */}
      <ConfirmDeleteModal
        isOpen={Boolean(deletingQuestion)}
        onClose={() => setQuestionToDelete(null)}
        onConfirm={confirmDeleteQuestion}
        title="Xóa câu hỏi"
        itemType="câu hỏi"
        itemName={deletingQuestion?.questionText}
        loading={deleteQuestionMutation.isPending}
      />

      {/* Confirm Delete Whole Question Set Modal */}
      <ConfirmDeleteModal
        isOpen={isConfirmDeleteSetOpen}
        onClose={() => setIsConfirmDeleteSetOpen(false)}
        onConfirm={confirmDeleteSet}
        title="Xóa bộ câu hỏi"
        itemType="bộ câu hỏi"
        itemName={currentSet?.title || currentSet?.name}
        loading={deleteSetMutation.isPending}
      />

      {/* Quota Exceeded Modal */}
      <QuotaExceededModal
        isOpen={quotaExceededModal.isOpen}
        onClose={() => setQuotaExceededModal(prev => ({ ...prev, isOpen: false }))}
        onOpenTopUp={onOpenTopUp}
        title={quotaExceededModal.title}
        message={quotaExceededModal.message}
        type={quotaExceededModal.type}
        currentTier={effectiveTier}
      />
    </div>
  );
};

